import React, { InputHTMLAttributes, forwardRef, useRef, useCallback } from "react";
import styled from "styled-components";

/* ex
      <CustomInput
        type="file"
        // previewImageUrl={uploadedImageUrl} // 예: 서버 업로드 후 set된 이미지 URL
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            // 이미지 업로드 후 URL 설정 (예: S3 업로드 등)
            // const fakeUrl = URL.createObjectURL(file); // 프리뷰 용도
            // setUploadedImageUrl(fakeUrl);
          }
        }}
      />
*/

type CustomInputProps = {
  label?: string;
  error?: string;
  type?: string;
  previewImageUrl?: string; // 이미지 썸네일 URL (optional)
} & InputHTMLAttributes<HTMLInputElement>;

export const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ label, error, id, type = "text", previewImageUrl, ...rest }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).slice(2, 9)}`;
    const internalRef = useRef<HTMLInputElement>(null);

    const handleClick = useCallback(() => {
      internalRef.current?.click();
    }, []);

    const sharedRef = (node: HTMLInputElement) => {
      internalRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    return (
      <InputWrapper>
        {label && <StyledLabel htmlFor={inputId}>{label}</StyledLabel>}

        {type === "file" ? (
          <>
            <HiddenInput
              id={inputId}
              type="file"
              ref={sharedRef}
              hasError={!!error}
              accept="image/*"
              {...rest}
            />
            <UploadBox type="button" onClick={handleClick}>
              {previewImageUrl ? (
                <PreviewImage src={previewImageUrl} alt="업로드된 이미지" />
              ) : (
                <PlusIcon>+</PlusIcon>
              )}
            </UploadBox>
          </>
        ) : (
          <StyledInput id={inputId} ref={ref} type={type} hasError={!!error} {...rest} />
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </InputWrapper>
    );
  }
);

CustomInput.displayName = "CustomInput";

export default CustomInput;

// ─── styled-components ───

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLabel = styled.label`
  margin-bottom: 4px;
  font-size: 14px;
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
  padding: 10px;
  border: 1px solid ${({ hasError }) => (hasError ? "red" : "#ccc")};
  border-radius: 4px;
  font-size: 14px;
`;

const HiddenInput = styled.input<{ hasError?: boolean }>`
  display: none;
`;

const UploadBox = styled.button`
  width: 120px;
  height: 120px;
  background: #fff;
  border: 1px dashed #bbb;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
`;

const PlusIcon = styled.span`
  font-size: 48px;
  color: #999;
  line-height: 1;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ErrorText = styled.span`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;
